import { motion } from "framer-motion";
import { colors, fonts } from "@/lib/theme";

interface HeaderProps {
  title?: string | null;
  description?: string | null;
}

/**
 * Option 1: Expanding Lines
 * Horizontal lines expand from center with corner brackets
 */
export function ExpandingLinesHeader({ title, description }: HeaderProps) {
  const hasContent = title || description;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8 relative overflow-hidden"
      style={{ backgroundColor: "#FAF8F6" }}
    >
      {/* Horizontal lines */}
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: "60%" }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
        className="absolute h-px"
        style={{ backgroundColor: colors.primary, opacity: 0.3, top: "calc(50% - 80px)" }}
      />
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: "60%" }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
        className="absolute h-px"
        style={{ backgroundColor: colors.primary, opacity: 0.3, top: "calc(50% + 80px)" }}
      />

      {/* Corner brackets - closer to edges on mobile */}
      <motion.div
        initial={{ width: 0, height: 0, opacity: 0 }}
        whileInView={{ width: 30, height: 30, opacity: 0.4 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
        className="absolute top-[20%] left-[8%] md:left-[15%] border-t border-l"
        style={{ borderColor: colors.primary }}
      />
      <motion.div
        initial={{ width: 0, height: 0, opacity: 0 }}
        whileInView={{ width: 30, height: 30, opacity: 0.4 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
        className="absolute top-[20%] right-[8%] md:right-[15%] border-t border-r"
        style={{ borderColor: colors.primary }}
      />
      <motion.div
        initial={{ width: 0, height: 0, opacity: 0 }}
        whileInView={{ width: 30, height: 30, opacity: 0.4 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
        className="absolute bottom-[20%] left-[8%] md:left-[15%] border-b border-l"
        style={{ borderColor: colors.primary }}
      />
      <motion.div
        initial={{ width: 0, height: 0, opacity: 0 }}
        whileInView={{ width: 30, height: 30, opacity: 0.4 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
        className="absolute bottom-[20%] right-[8%] md:right-[15%] border-b border-r"
        style={{ borderColor: colors.primary }}
      />

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
        className="text-center z-10 max-w-2xl"
      >
        {title && (
          <h2
            className="text-2xl sm:text-3xl md:text-5xl mb-4 md:mb-6"
            style={{
              fontFamily: fonts.serif,
              color: colors.text,
              fontWeight: 400,
              letterSpacing: "0.05em",
            }}
          >
            {title}
          </h2>
        )}

        {description && (
          <p
            className="text-base md:text-lg"
            style={{
              fontFamily: fonts.sans,
              color: colors.textMuted,
              lineHeight: 1.8,
            }}
          >
            {description}
          </p>
        )}

        {!hasContent && (
          <div
            style={{
              width: 60,
              height: 2,
              backgroundColor: colors.primaryMuted,
              margin: "0 auto",
            }}
          />
        )}
      </motion.div>
    </div>
  );
}
